import { useRef, useState } from 'react'
import { Trash2, UploadCloud } from 'lucide-react'
import { toast } from 'sonner'
import { Button, Input } from '@/components/ui'
import { useDeleteCasePhoto, useUploadCasePhoto } from '../api/queries'
import type { CasePhoto } from '../api/types'

const ACCEPT_ATTR = 'image/*'

export interface CasePhotoGalleryProps {
  caseId: string
  photos: CasePhoto[]
  disabled?: boolean
}

/**
 * Case-level photo evidence gallery — drop-zone + thumbnail grid.
 * Mirrors `sales-inbox/components/SalesIntakeUploadDrawer.tsx`'s onDrop/
 * file-input pattern (the closest existing precedent) but inline rather
 * than its own Drawer, since photos are a section within the case
 * detail drawer, not a standalone flow with a multi-file processing
 * queue. Caption is entered once per upload batch, not editable after
 * the fact — keep it simple until there's a real need for more.
 */
export function CasePhotoGallery({ caseId, photos, disabled = false }: CasePhotoGalleryProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [caption, setCaption] = useState('')
  const upload = useUploadCasePhoto()
  const remove = useDeleteCasePhoto()

  function uploadFiles(files: FileList | File[]) {
    for (const file of Array.from(files)) {
      upload.mutate(
        { caseId, file, caption: caption || undefined },
        {
          onError: (err) =>
            toast.error(`Upload failed${err instanceof Error ? `: ${err.message}` : ''}`),
        },
      )
    }
    setCaption('')
  }

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files
    if (files && files.length > 0) uploadFiles(files)
    e.target.value = ''
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (disabled) return
    const files = e.dataTransfer?.files
    if (files && files.length > 0) uploadFiles(files)
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <div className="flex flex-col gap-3">
      {!disabled ? (
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          className="flex flex-col items-center justify-center gap-2 rounded-sm border-2 border-dashed border-border-default bg-surface-sunk px-4 py-6 text-center"
        >
          <UploadCloud size={22} className="text-tertiary" />
          <p className="m-0 text-xs text-secondary">Drag &amp; drop photos here, or</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT_ATTR}
            className="hidden"
            onChange={onPick}
          />
          <div className="flex w-full max-w-sm items-center gap-2">
            <Input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Caption (optional)"
              className="flex-1"
            />
            <Button
              variant="secondary"
              size="sm"
              loading={upload.isPending}
              onClick={() => fileInputRef.current?.click()}
            >
              Choose photos
            </Button>
          </div>
        </div>
      ) : null}

      {photos.length === 0 ? (
        <p className="m-0 text-sm text-tertiary">No photos attached yet.</p>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {photos.map((photo) => (
            <div
              key={photo.document_id}
              className="flex flex-col gap-1 rounded-sm border border-border-subtle bg-surface p-2"
            >
              <a href={photo.url} target="_blank" rel="noreferrer">
                <img
                  src={photo.url}
                  alt={photo.caption || photo.filename}
                  className="h-28 w-full rounded-xs object-cover"
                />
              </a>
              {photo.caption ? (
                <span className="line-clamp-2 text-xs text-secondary" title={photo.caption}>
                  {photo.caption}
                </span>
              ) : null}
              {!disabled ? (
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label={`Remove ${photo.filename}`}
                  onClick={() =>
                    remove.mutate(
                      { caseId, documentId: photo.document_id },
                      {
                        onError: (err) =>
                          toast.error(
                            `Remove failed${err instanceof Error ? `: ${err.message}` : ''}`,
                          ),
                      },
                    )
                  }
                >
                  <Trash2 size={14} />
                </Button>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
